import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, vModelText as _vModelText, withDirectives as _withDirectives, createVNode as _createVNode, Transition as _Transition, withCtx as _withCtx, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = ["innerHTML"]
const _hoisted_2 = {
  key: 0,
  class: "f-autocomplete__required"
}
const _hoisted_3 = {
  key: 0,
  class: "f-autocomplete__pop-up"
}
const _hoisted_4 = { class: "f-autocomplete__modal-search" }
const _hoisted_5 = { class: "f-autocomplete__modal-input" }
const _hoisted_6 = ["placeholder"]

import { computed, getCurrentInstance, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { debounce } from 'lodash-es';
import { ModalRef } from '@/types/elements/modal';
import useDevice from '@/hooks/useDevice';
import { formAutoCompleteFns, FormAutoCompleteProps } from '@/types/form/form-auto-complete';
import { FormDropdownOption } from '@/types/form/form-dropdown/form-dropdown';
import { FormElMsgData } from '@/types/form/others/form-el-msg';
import FormElMsg from '@/form/other/FormElMsg.vue';
import utils from '@/utils';
import { SitecoreContext } from '@/types/layout';
import { FormInputRef } from '@/types/form/form-input';
import { DynamicFormContext } from '@/types/form/dynamic-form';
import FormAutoCompleteOptions from '@/form/FormAutoComplete/FormAutoCompleteOptions.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'FormAutoComplete',
  props: {
    item: {}
  },
  emits: ['search', 'change'],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const { isMobileOrTablet } = useDevice();
const app = getCurrentInstance();
const $jss = app?.appContext.config.globalProperties.$jss;
const sitecoreContext = $jss?.sitecoreContext() as SitecoreContext;
const lang = sitecoreContext?.language;
const formContext = inject<DynamicFormContext>('formContext')!;
const emit = __emit;
const props = __props;
const rootEl = ref();
const inputEl = ref<HTMLInputElement>();
const modalSearchEl = ref();
const value = ref('');
const options = ref<FormDropdownOption[]>([]);
const loading = ref(true);
const focused = ref(false);
const optionsModalRef = ref<ModalRef>();
const isRequired = ref(props.item?.fields?.required?.value ?? false);
const isDisabled = ref(false);
const isVisible = ref(true);
const msg = ref<FormElMsgData>();
const fields = computed(() => props.item?.fields);
const hasValue = computed(() => !utils.obj.isNoE(value.value));
const label = computed(() => fields.value?.label?.value || '');
const placeholder = computed(() => fields.value?.placeholder?.value || '');
const fieldName = computed(() => fields.value?.fieldName?.value || '');
const search = async (val: string) => {
  const type = utils.sp.getVal(fields.value?.type);
  const alpha2Code = utils.i18n.getAlpha2Code(lang);
  if (!type) return;
  loading.value = true;
  options.value = await formAutoCompleteFns[type]({
    country: alpha2Code,
    keywords: val,
    limit: 8,
  });
  loading.value = false;
};
const onInput = debounce(async (e) => {
  if (isMobileOrTablet.value) return;
  const val = e.target.value.trim();
  msg.value = undefined;
  await search(val);
}, 600);
const onPopInput = debounce(async (e) => {
  const val = e.target.value.trim();
  msg.value = undefined;
  await search(val);
}, 600);
const onFocus = async (e: any) => {
  focused.value = true;
  if (isMobileOrTablet.value) {
    await optionsModalRef.value?.open();
    modalSearchEl.value?.focus();
  }
};
const onMainClick = () => {
  inputEl.value?.focus();
};
const onOptionClick = (option: FormDropdownOption) => {
  focused.value = false;
  value.value = option.value;
  emit('change', 'form-auto-complete', fieldName.value, option.value, option);
  if (isMobileOrTablet.value) {
    optionsModalRef.value?.close();
  }
};
const onSearchIconClick = async (e: any) => {
  focused.value = true;
  if (isMobileOrTablet.value) {
    await optionsModalRef.value?.open();
    modalSearchEl.value?.focus();
  } else {
    e.currentTarget.previousSibling.focus();
  }
};
const onBodyClick = (e: any) => {
  if (isMobileOrTablet.value) return;
  if (
    !rootEl.value.contains(e.target) &&
    rootEl.value !== e.target &&
    !optionsModalRef.value?.el.value?.contains(e.target) &&
    optionsModalRef.value?.el.value !== e.target
  ) {
    focused.value = false;
  }
};
const setRequired = (required: boolean) => {
  isRequired.value = required;
};

const setDisabled = (disabled: boolean) => {
  isDisabled.value = disabled;
};

const setVisible = (visible: boolean) => {
  isVisible.value = visible;
};

const updateValue = (newValue?: string | null) => {
  value.value = newValue || '';
};

const getValue = (newValue?: string | null) => {
  return value.value;
};

const validate = async () => {
  const result = {
    value: value.value,
    valid: true,
    message: '',
  };

  if (!isVisible.value) {
    return result;
  }

  if (isRequired.value && utils.obj.isNoE(value.value)) {
    result.valid = false;
    result.message = fields.value?.requiredMsg?.value || 'This field is required';
    msg.value = {
      type: 'error',
      message: result.message,
    };
    return result;
  }
  return result;
};
watch(
  () => [isMobileOrTablet, focused],
  ([_mt, _f]) => {
    if (_mt.value && _f.value) {
      optionsModalRef.value?.open();
    } else {
      optionsModalRef.value?.close();
    }
  }
);
const getRefData = (): FormInputRef => ({
  el: rootEl.value!,
  type: 'form-auto-complete',
  name: fieldName.value,
  id: props.item?.id ?? '',
  visible: isVisible.value,
  setRequired,
  setDisabled,
  setVisible,
  updateValue,
  getValue,
  validate,
});
watch(
  () => props.item,
  () => {
    formContext.regFormEl(getRefData());
  },
  { immediate: true }
);
onMounted(() => {
  formContext.regFormEl(getRefData());
  document.body.addEventListener('click', onBodyClick);
});
onBeforeUnmount(() => {
  document.body.removeEventListener('click', onBodyClick);
  formContext.unRegFormEl(getRefData());
});

__expose(getRefData());

return (_ctx: any,_cache: any) => {
  const _component_icon = _resolveComponent("icon")!
  const _component_modal = _resolveComponent("modal")!

  return (fields.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: "f-autocomplete",
        ref_key: "rootEl",
        ref: rootEl
      }, [
        _createElementVNode("div", {
          class: _normalizeClass(["f-autocomplete__content", [{ active: focused.value }]])
        }, [
          _createElementVNode("div", {
            class: "f-autocomplete__main",
            onClick: onMainClick
          }, [
            _createElementVNode("div", {
              class: _normalizeClass(["f-autocomplete__placeholder", [{ 'as-label': focused.value || hasValue.value }]])
            }, [
              _createElementVNode("span", {
                class: "f-autocomplete__label",
                innerHTML: focused.value || hasValue.value ? label.value : _unref(utils).string.ifEmpty(placeholder.value, label.value)
              }, null, 8, _hoisted_1),
              (isRequired.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_2, "*"))
                : _createCommentVNode("", true)
            ], 2),
            _withDirectives(_createElementVNode("input", {
              type: "text",
              onInput: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(onInput) && _unref(onInput)(...args))),
              onFocus: onFocus,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((value).value = $event)),
              ref_key: "inputEl",
              ref: inputEl
            }, null, 544), [
              [_vModelText, value.value]
            ])
          ]),
          _createVNode(_Transition, { name: "options-ani" }, {
            default: _withCtx(() => [
              (!_unref(isMobileOrTablet) && value.value.length > 1 && focused.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    _createVNode(FormAutoCompleteOptions, {
                      loading: loading.value,
                      options: options.value,
                      value: value.value,
                      onOptionClick: onOptionClick
                    }, null, 8, ["loading", "options", "value"])
                  ]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          })
        ], 2),
        _createVNode(FormElMsg, { msg: msg.value }, null, 8, ["msg"]),
        _createVNode(_component_modal, {
          class: "f-autocomplete__modal",
          ref_key: "optionsModalRef",
          ref: optionsModalRef,
          animation: "bottom-slide-in",
          "fire-gtm": false,
          closable: "",
          sticky: false
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(_component_icon, {
                  name: "search",
                  size: "xxs",
                  onClick: onSearchIconClick
                }),
                _withDirectives(_createElementVNode("input", {
                  type: "text",
                  placeholder: fields.value.placeholder.value,
                  onInput: _cache[2] || (_cache[2] = 
//@ts-ignore
(...args) => (_unref(onPopInput) && _unref(onPopInput)(...args))),
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((value).value = $event)),
                  ref_key: "modalSearchEl",
                  ref: modalSearchEl
                }, null, 40, _hoisted_6), [
                  [_vModelText, value.value]
                ])
              ])
            ]),
            _createVNode(FormAutoCompleteOptions, {
              loading: loading.value && value.value.length > 1,
              options: options.value,
              value: value.value,
              onOptionClick: onOptionClick
            }, null, 8, ["loading", "options", "value"])
          ]),
          _: 1
        }, 512)
      ], 512))
    : _createCommentVNode("", true)
}
}

})